import { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Column } from 'react-table'
import Table from '../../../global/components/table/Table'
import BasicFilter from '../../../global/components/table/helpers/filters/BasicFilter'
import DateFilter from '../../../global/components/table/helpers/filters/DateFilter'
import DropdownFilter from '../../../global/components/table/helpers/filters/DropdownFilter'
import { handleColumnFilterUpdate } from '../../../global/components/table/helpers/multiColumnFilters'
import { ILocationFilters } from '../main/QueuesMain'
import WorkOrderDetailModal from '../../../work-orders/components/work-order-detail/components/main/WorkOrderDetailModal'
import { WoDetailContext } from '../../../work-orders/components/context/context'
import { IDeliveryCandidate } from '../../../../../app/entities/DeliveryCandidate'
import fetchDataAndSetTable from '../../../global/components/table/helpers/fetchDataAndSetTable'
import NoFilter from '../../../global/components/table/helpers/filters/NoFilter'
import LocationFilter from '../../../global/components/table/helpers/filters/LocationFilter'
import { DELIVERY_CANDIDATE_STATUS } from '../../../global/constants/delivery-candidate'
import RejectDeliveryCandidateModal from '../sub-modals/RejectDeliveryCandidateModal'
import ForceDeliveryCandidateAppointmentModal from '../sub-modals/ForceDeliveryCandidateAppointmentModal'
import { fetchData } from '../../../global/utils/fetch'
import { rejectDeliveryCandidate, forceDeliveryCandidateAppointment } from '../api'
import { ToastNotificationContext } from '../../../global/context/toast-context/ToastNotificationContext'

interface ITireRackDeliveriesTableProps {
  locationFilters: ILocationFilters
}

function getStatusColor(status: DELIVERY_CANDIDATE_STATUS) {
  switch (status) {
    case DELIVERY_CANDIDATE_STATUS.AWAITING_SCHEDULING:
      return '#FFA500' // Orange
    case DELIVERY_CANDIDATE_STATUS.ORDER_CREATED:
      return '#008000' // Green
    case DELIVERY_CANDIDATE_STATUS.CANDIDATE_REJECTED:
      return '#FF0000' // Red
  }
}

const BASE_URL = `/queues/tire-rack-deliveries`

function TireRackDeliveriesTable(props: ITireRackDeliveriesTableProps) {
  const { locationFilters } = props
  const { queryOrderData, clearData } = useContext(WoDetailContext)
  const { toastStatus, setToastStatus } = useContext(ToastNotificationContext)
  const navigate = useNavigate()
  const [workOrderDetailsModalOpen, setWorkOrderDetailsModalOpen] =
    useState<boolean>(false)
  const [rejectModalOpen, setRejectModalOpen] = useState<boolean>(false)
  const [forceAppointmentModalOpen, setForceAppointmentModalOpen] =
    useState<boolean>(false)
  const [selectedDeliveryCandidate, setSelectedDeliveryCandidate] =
    useState<IDeliveryCandidate | null>(null)
  const [tableState, setTableState] = useState({
    data: [] as IDeliveryCandidate[],
    isLoading: false,
    count: 0,
    pageSize: 100,
    offSet: 0,
  })
  const [columnFilters, setColumnFilters] = useState({})
  const [deliveryDateFilter, setDeliveryDateFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [locationColumnFilter, setLocationColumnFilter] = useState('')

  const statusItems = Object.values(DELIVERY_CANDIDATE_STATUS).map(
    (status) => ({
      key: status,
      value: status
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' '),
    }),
  )

  async function handleWorkOrderDetailsModalOpen(
    deliveryCandidate: IDeliveryCandidate,
  ) {
    if (deliveryCandidate.status !== DELIVERY_CANDIDATE_STATUS.ORDER_CREATED)
      return
    setWorkOrderDetailsModalOpen(true)
    const url = `${BASE_URL}/${deliveryCandidate.objectId}`
    navigate(url)
    await queryOrderData(deliveryCandidate.objectId) //the for deliveries we make provide a custom orderObjectId which is the same as the deliveryCandidate.objectId
  }

  async function handleDetailsClosed() {
    setWorkOrderDetailsModalOpen(false)
    navigate(BASE_URL)
    clearData()
    refetchTable()
  }

  function handleRowClick(row: any) {
    const deliveryCandidate = row.original as IDeliveryCandidate
    if (
      deliveryCandidate.status === DELIVERY_CANDIDATE_STATUS.AWAITING_SCHEDULING
    ) {
      setSelectedDeliveryCandidate(deliveryCandidate)
      setRejectModalOpen(true)
    } else if (
      deliveryCandidate.status === DELIVERY_CANDIDATE_STATUS.ORDER_CREATED
    ) {
      handleWorkOrderDetailsModalOpen(deliveryCandidate)
    } else if (
      deliveryCandidate.status === DELIVERY_CANDIDATE_STATUS.CANDIDATE_REJECTED
    ) {
      setSelectedDeliveryCandidate(deliveryCandidate)
      setForceAppointmentModalOpen(true)
    }
  }

  function onColumnFilterUpdate(filtersUpdate: object) {
    handleColumnFilterUpdate(filtersUpdate, columnFilters, setColumnFilters)
  }

  async function handleRejectDeliveryCandidate() {
    if (!selectedDeliveryCandidate) return

    await fetchData<void>(
      rejectDeliveryCandidate(selectedDeliveryCandidate.objectId),
    )

    setRejectModalOpen(false)
    setSelectedDeliveryCandidate(null)
    refetchTable()
  }

  function handleCloseRejectModal() {
    setRejectModalOpen(false)
    setSelectedDeliveryCandidate(null)
  }

  async function handleForceAppointment(
    routeObjectId: string,
    routeInstructionTime: string,
  ) {
    if (!selectedDeliveryCandidate) return

    try {
      await fetchData<void>(
        forceDeliveryCandidateAppointment(
          selectedDeliveryCandidate.objectId,
          routeObjectId,
          routeInstructionTime,
        ),
      )
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: 'Successfully forced appointment!',
        severity: 'success',
      })
    } catch (error) {
      console.error(error)
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: 'There has been an error forcing appointment.',
        severity: 'error',
      })
    }

    setForceAppointmentModalOpen(false)
    setSelectedDeliveryCandidate(null)
    refetchTable()
  }

  function handleCloseForceAppointmentModal() {
    setForceAppointmentModalOpen(false)
    setSelectedDeliveryCandidate(null)
  }

  const tireRackDeliveryQueueEndpoint = `${process.env.REACT_APP_ORIGIN}/delivery-candidates/queue`
  function refetchTable() {
    fetchDataAndSetTable<IDeliveryCandidate>({
      endpoint: tireRackDeliveryQueueEndpoint,
      queries: [
        { pageSize: tableState.pageSize.toString() },
        { offSet: tableState.offSet.toString() },
        { columnFilters: JSON.stringify(columnFilters) },
        { selectedLocationId: locationFilters.locationFilter },
      ],
      setTableState,
    })
  }

  useEffect(() => {
    refetchTable()
  }, [columnFilters, locationFilters, tableState.pageSize, tableState.offSet])

  const columns: Column<IDeliveryCandidate | any>[] = [
    //because tireRackDeliverData is a JSON object, we need to access its properties
    //with the columnFilters using the method that has been here implemented with the
    //static text additions 🙃
    {
      Header: 'Status',
      accessor: 'status',
      Cell: ({ row: { original } }) => {
        const { status } = original as IDeliveryCandidate
        return (
          <div
            style={{
              padding: '4px 8px',
              borderRadius: '5px',
              border: `1px solid ${getStatusColor(status)}`,
              color: getStatusColor(status),
              fontSize: '12px',
              textAlign: 'center',
              whiteSpace: 'nowrap',
            }}
          >
            {status
              .split('-')
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ')}
          </div>
        )
      },
      Filter: DropdownFilter({
        value: statusFilter,
        onChange: (val: string) => {
          setStatusFilter(val)
          onColumnFilterUpdate({
            status: val,
          })
        },
        items: statusItems,
      }),
    },
    {
      Header: 'TR Order ID',
      accessor: (row: IDeliveryCandidate) =>
        row.tireRackDeliveryData.tireRackOrderNumber,
      Filter: BasicFilter({
        onChange: (val: string) =>
          onColumnFilterUpdate({
            tireRackDeliveryData: `"tireRackOrderNumber": "%${val}%"`,
          }),
      }),
    },
    {
      Header: 'Delivery Date',
      accessor: (row: IDeliveryCandidate) => {
        const deliveryDate = new Date(row.deliveryDate)
        const [year, month, day] = deliveryDate
          .toISOString()
          .split('T')[0]
          .split('-')
        return `${month}/${day}/${year.slice(2, 4)}`
      },
      Filter: DateFilter({
        value: deliveryDateFilter,
        onChange: (val: string) => {
          setDeliveryDateFilter(val)
          onColumnFilterUpdate({
            deliveryDate: val,
          })
        },
      }),
    },
    {
      Header: 'Location Name',
      accessor: (row: IDeliveryCandidate) => row.location.name,
      Filter: LocationFilter({
        value: locationColumnFilter,
        onChange: (val: string) => {
          setLocationColumnFilter(val)
          onColumnFilterUpdate({
            location: { name: val },
          })
        },
      }),
    },
    {
      Header: 'Customer Full Name',
      accessor: (row: IDeliveryCandidate) =>
        row.tireRackDeliveryData.customerFullName,
      Filter: BasicFilter({
        onChange: (val: string) =>
          onColumnFilterUpdate({
            tireRackDeliveryData: `"customerFullName": "%${val.toUpperCase()}%"`,
          }),
      }),
    },
    {
      Header: 'Delivery Address',
      accessor: (row: IDeliveryCandidate) => {
        const { tireRackDeliveryData: data } = row
        return `${data.installationPointAddress}, ${data.installationPointCity}, ${data.installationPointState} ${data.installationPointZipCode}`
      },
      Filter: BasicFilter({
        onChange: (val: string) =>
          onColumnFilterUpdate({
            tireRackDeliveryData: `"installationPointAddress": "%${val.toUpperCase()}%"`,
          }),
      }),
    },
    {
      Header: 'Customer Phone',
      accessor: (row: IDeliveryCandidate) =>
        row.tireRackDeliveryData.customerPhoneNumber,
      Filter: BasicFilter({
        onChange: (val: string) =>
          onColumnFilterUpdate({
            tireRackDeliveryData: `"customerPhoneNumber": "%${val}%"`,
          }),
      }),
    },
    {
      Header: 'Customer Email',
      accessor: (row: IDeliveryCandidate) =>
        row.tireRackDeliveryData.customerEmail,
      Filter: BasicFilter({
        onChange: (val: string) =>
          onColumnFilterUpdate({
            tireRackDeliveryData: `"customerEmail": "%${val}%"`,
          }),
      }),
    },
    {
      Header: 'Customer Vehicle',
      accessor: (row: IDeliveryCandidate) => {
        const { tireRackDeliveryData: data } = row
        return `${data.vehicleYearAbbreviated} ${
          data.vehicleBrandAbbreviated
        } ${data.vehicleModelName} ${data.vehicleModelVariant || ''}`.trim()
      },
      Filter: NoFilter(),
    },
    {
      Header: 'Tire Qty',
      accessor: 'tires',
      Cell: ({ row: { original } }) => {
        const { tireRackDeliveryData } = original as IDeliveryCandidate
        const tiresQuantity = tireRackDeliveryData.tireQuantity
        return <>{tiresQuantity}</>
      },
      Filter: BasicFilter({
        onChange: (val: string) =>
          onColumnFilterUpdate({
            tireRackDeliveryData: `"tireQuantity": "%${val}%"`,
          }),
      }),
    },
    {
      Header: 'Tire Description',
      accessor: (row: IDeliveryCandidate) => {
        const { tireRackDeliveryData: data } = row
        return `${data.tireManufacturer} - ${data.tireDescription}`
      },
      Filter: NoFilter(),
    },
    {
      Header: 'Freight Cost',
      accessor: 'freightCost',
      Cell: ({ row: { original } }) => {
        const { tireRackDeliveryData } = original as IDeliveryCandidate
        const freightCost = tireRackDeliveryData.freightTotal
        return <>${freightCost}</>
      },
      Filter: BasicFilter({
        onChange: (val: string) =>
          onColumnFilterUpdate({
            tireRackDeliveryData: `"freightTotal": "%${val}%"`,
          }),
      }),
    },
  ]
  return (
    <div style={{ width: '100%' }}>
      <WorkOrderDetailModal
        handleClosed={handleDetailsClosed}
        isOpen={workOrderDetailsModalOpen}
      />
      <RejectDeliveryCandidateModal
        isOpen={rejectModalOpen}
        deliveryCandidate={selectedDeliveryCandidate}
        handleClose={handleCloseRejectModal}
        handleConfirm={handleRejectDeliveryCandidate}
      />
      <ForceDeliveryCandidateAppointmentModal
        isOpen={forceAppointmentModalOpen}
        deliveryCandidate={selectedDeliveryCandidate}
        handleClose={handleCloseForceAppointmentModal}
        handleConfirm={handleForceAppointment}
      />
      <div
        style={{
          marginBottom: '10px',
          fontSize: '14px',
          color: '#555',
          fontStyle: 'italic',
        }}
      >
        Only Tire Rack Deliveries currently eligible, or already scheduled, for
        delivery on the next available day are displayed here. Please go to the
        Work Orders page for all scheduled deliveries.
      </div>
      <Table
        columns={columns}
        data={tableState.data ?? []}
        isLoading={tableState.isLoading}
        onRowClick={handleRowClick}
        pagination={{ setTableState, tableState }}
        isTableTopperPresent={false}
      />
    </div>
  )
}

export default TireRackDeliveriesTable
